import type { DataTableColumn, PaginationInfo } from "naive-ui";
import type { ComponentPublicInstance, Ref, VNode } from "vue";
import { computed, h, reactive, ref, shallowRef, unref } from "vue";
import DataTableBodyWidget from "../widgets/DataTableBodyWidget.vue";
import DataTableFooterWidget from "../widgets/DataTableFooterWidget.vue";
import DataTableHeadWidget from "../widgets/DataTableHeadWidget.vue";
import type { FormField, FormModel } from "../widgets/FormFieldsWidget.vue";

export interface FilterDto {
  key: string;
  type: string;
  values: string[];
}

export interface DataTableQueryDto {
  page?: number;
  size?: number;
  offset?: number;
  filters?: FilterDto[];
  search?: string;
  columns?: string[];
  order?: "asc" | "desc";
  order_by?: string;
}

export type DataTableColumnFilter = Omit<FormField, "name">;

/** Payload yang dikirim ke callback export (tanpa pagination) */
export interface DataTableExportPayload {
  columns: string[];
  filters?: FilterDto[];
  search?: string;
  order?: "asc" | "desc";
  order_by?: string;
  /** Keys sheet rincian yang dipilih user di modal export */
  detailSheets?: string[];
}

/** Konfigurasi tombol export di toolbar datatable */
export interface DataTableExportDetailSheet {
  /** Key relasi di data row (mis. "formalEducations") */
  key: string;
  /** Nama sheet Excel (maks 31 karakter) */
  name: string;
  /** Label di modal export. Default: name */
  label?: string;
}

/** Konfigurasi tombol export di toolbar datatable */
export interface DataTableExportConfig {
  /** Dipanggil saat user konfirmasi export di modal */
  onClick: (payload: DataTableExportPayload) => void | Promise<void>;
  /** Default true */
  enable?: boolean;
  /** Sheet rincian (relasi one-to-many) yang bisa dipilih di modal export. Kalau tidak diisi, tidak ada pilihan sheet. */
  detailSheets?: DataTableExportDetailSheet[];
}

export type DataTableColumnDef<T> =
  | (DataTableColumn<T> & {
      filter?: DataTableColumnFilter;
      hidden?: boolean;
    })
  | Record<string, any>;

/**
 * Tombol tambahan di toolbar datatable (sejajar dengan tombol "+").
 * `visible` bisa boolean atau fungsi (dipanggil saat render, jadi reaktif
 * terhadap ref/computed yang dibaca di dalamnya). Default: tampil.
 */
export interface DataTableAction {
  key: string;
  label?: string;
  /** nama icon iconify, mis. "mdi:upload" */
  icon?: string;
  type?: "default" | "primary" | "info" | "success" | "warning" | "error";
  visible?: boolean | (() => boolean);
  onClick: () => void;
}

/** Konfigurasi checkbox (kolom selection, fixed di kiri) */
export interface DataTableSelection<T> {
  enable: boolean;
  /** Row yang checkbox-nya di-disable */
  disabled?: (row: T) => boolean;
}

/** Aksi massal: muncul di bar atas tabel saat ada row yang dipilih */
export interface DataTableBulkAction<T> {
  key: string;
  label?: string;
  /** nama icon iconify, mis. "mdi:send" */
  icon?: string;
  type?: "default" | "primary" | "info" | "success" | "warning" | "error";
  visible?: boolean | ((rows: T[]) => boolean);
  disabled?: boolean | ((rows: T[]) => boolean);
  /** Default true: kosongkan pilihan + refresh data setelah onClick sukses */
  clearAfter?: boolean;
  onClick: (rows: T[]) => void | Promise<void>;
}

/** Target default panel filter (sidebar layout) */
export const DEFAULT_FILTER_TELEPORT_TARGET = "#layout-slot-sidebar";

/**
 * Target teleport panel filter. Bisa:
 * - string id tanpa "#"  : "my-sidebar"        -> "#my-sidebar"
 * - string selector      : "#my-sidebar" / ".my-class"
 * - template ref         : ref<HTMLElement | null>(null) (elemen DOM atau instance komponen)
 * - elemen DOM langsung  : document.getElementById("...")
 */
export type FilterTeleportTarget = string | HTMLElement | Readonly<Ref<string | HTMLElement | ComponentPublicInstance | null | undefined>>;

export interface DataTableParams<T> {
  columns: DataTableColumnDef<T>[];
  defaultVisibleColumns: string[];
  dataFetcher: (payload: DataTableQueryDto) => Promise<T[]>;
  pageFetcher: (payload: DataTableQueryDto) => Promise<{
    total: number;
    filteredTotal: number;
  }>;
  onRowClick?: ((row: T) => void) | null;
  key: string;
  create?: {
    enable: boolean;
    onClick: () => void;
  };
  /** Tombol export di toolbar + modal preset kolom & filter. Kalau tidak diisi, tombolnya tidak muncul. */
  export?: DataTableExportConfig;
  actions?: DataTableAction[];
  /** Checkbox di kiri (fixed left). Opsional: kalau tidak diisi, tidak ada checkbox */
  selection?: DataTableSelection<T>;
  /** Aksi massal untuk row terpilih */
  bulkActions?: DataTableBulkAction<T>[];
  /** Tujuan teleport panel filter. Default: "#layout-slot-sidebar" */
  filterTeleportTo?: FilterTeleportTarget;
  /** Tinggi maksimal body tabel (mis. "200px" atau 200). Opsional: kosong = tidak dibatasi. */
  maxHeight?: string | number;
  /**
   * Hitung max-height body tabel otomatis (sisa tinggi viewport) sehingga hanya body
   * n-data-table yang scroll dan header kolomnya tetap diam. Kalau true, `maxHeight` diabaikan.
   */
  fillHeight?: boolean;
  /** Jarak (px) di bawah pagination saat `fillHeight`. Default 12. */
  bottomOffset?: number;
  uniqueKey: string;
}

export type NamedDataTableColumn<T> = DataTableColumnDef<T> & { key: string; title?: string | (() => unknown) };

function isNamedColumn<T>(col: DataTableColumnDef<T>): col is NamedDataTableColumn<T> {
  return "key" in col && typeof (col as any).key === "string" && (col as any).key.length > 0;
}

function columnLabel<T>(col: NamedDataTableColumn<T>): string {
  return typeof col.title === "string" ? col.title : col.key;
}

export type DataTableSize = "small" | "medium" | "large";

/** Ukuran tabel yang tersedia di n-data-table (naive-ui) */
export const DATA_TABLE_SIZES: DataTableSize[] = ["small", "medium", "large"];

interface SavedTableState {
  visibleColumns?: string[];
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  search?: string;
  limit?: number;
  page?: number;
  filterModel?: FormModel;
  isFilterOpen?: boolean;
  size?: DataTableSize;
}

export function useDataTableWidget<T extends Record<string, any>>(params: DataTableParams<T>) {
  const storageKey = `datatable_state_${params.key}`;

  function loadSavedState(): SavedTableState {
    try {
      if (typeof window === "undefined") return {};
      const item = localStorage.getItem(storageKey);
      return item ? JSON.parse(item) : {};
    } catch (e) {
      console.error("Failed to load table state from localStorage", e);
      return {};
    }
  }

  function saveState(newState: SavedTableState) {
    try {
      if (typeof window === "undefined") return;
      const currentState = loadSavedState();
      const updated = { ...currentState, ...newState };
      localStorage.setItem(storageKey, JSON.stringify(updated));
    } catch (e) {
      console.error("Failed to save table state to localStorage", e);
    }
  }

  const uniqueKey = ref(params.uniqueKey);

  const savedState = loadSavedState();

  const columns = ref<DataTableColumnDef<T>[]>(params.columns.filter((x) => x.hidden !== true));
  const data = ref<T[]>([]);
  const total = ref<number>(0);
  const filteredTotal = ref<number>(0);

  // Memuat state pagination (page dan limit) dari localStorage
  const page = ref<number>(savedState.page ?? 1);
  const limit = ref<number>(savedState.limit ?? 20);

  const search = ref<string>(savedState.search ?? "");
  const isFilterOpen = ref<boolean>(savedState.isFilterOpen ?? false);

  // Ukuran tabel (naive-ui: small | medium | large), dipersist ke localStorage
  const size = ref<DataTableSize>(savedState.size && DATA_TABLE_SIZES.includes(savedState.size) ? savedState.size : "small");

  function setSize(next: DataTableSize) {
    size.value = next;
    saveState({ size: next });
  }

  // Flat sorting state
  const sortOrder = ref<"asc" | "desc" | undefined>(savedState.sortOrder);
  const sortBy = ref<string | undefined>(savedState.sortBy);

  // Visible columns state
  const allNamedColumnKeys = columns.value.filter(isNamedColumn).map((c) => c.key);
  const initialVisibleColumns = savedState.visibleColumns ? savedState.visibleColumns.filter((k) => allNamedColumnKeys.includes(k)) : null;

  const visibleColumns = ref<string[]>(initialVisibleColumns && initialVisibleColumns.length > 0 ? initialVisibleColumns : [...params.defaultVisibleColumns]);

  const filterFields = columns.value
    .filter((x): x is NamedDataTableColumn<T> => isNamedColumn(x) && !!x.filter)
    .map((x) => ({
      ...x.filter!,
      name: x.key as any,
    }));

  function buildDefaultFilterModel(): FormModel {
    return filterFields.reduce((acc, field) => {
      acc[field.name] = field.type === "number" ? null : null;
      return acc;
    }, {} as FormModel);
  }

  const filterModel = reactive<FormModel>({
    ...buildDefaultFilterModel(),
    ...(savedState.filterModel || {}),
  });

  // Snapshot of the filter model as it was when last "applied" (Filter button / Reset)
  // — used for activeFilterCount so typing in the draft form doesn't count as active.
  const appliedFilterModel = ref<FormModel>({
    ...buildDefaultFilterModel(),
    ...(savedState.filterModel || {}),
  });

  function resetFilter() {
    const defaults = buildDefaultFilterModel();

    Object.keys(filterModel).forEach((key) => {
      if (!(key in defaults)) delete filterModel[key];
    });

    Object.assign(filterModel, defaults);
    page.value = 1;
    saveState({ filterModel: { ...filterModel }, page: 1 });

    applyFilter();
  }

  const isDataLoading = ref<boolean>(true);
  const isPageLoading = ref<boolean>(true);

  const onRowClick = params.onRowClick;

  const pageSizes = [10, 20, 50, 100];

  // Tiga bagian datatable, bisa dipakai satu per satu di template:
  //   <component :is="table.headComponent.value" />
  //   <component :is="table.bodyComponent.value" />
  //   <component :is="table.footerComponent.value" />
  // Ketiganya diisi saat `init()` dipanggil.
  const headComponent = shallowRef<VNode | null>(null);
  const bodyComponent = shallowRef<VNode | null>(null);
  const footerComponent = shallowRef<VNode | null>(null);

  const pageCount = computed(() => {
    if (filteredTotal.value === 0 || limit.value === 0) return 1;
    return Math.ceil(filteredTotal.value / limit.value);
  });

  const isShowVisibleColumnModal = ref(false);

  const create = params.create;

  // Pengaturan tinggi body tabel (dibaca oleh DataTableBodyWidget)
  const maxHeight = params.maxHeight;
  const fillHeight = params.fillHeight ?? false;
  const bottomOffset = params.bottomOffset;

  // Tombol tambahan di toolbar (sejajar tombol "+")
  const actions = params.actions ?? [];

  function isActionVisible(action: DataTableAction): boolean {
    const v = action.visible;
    return typeof v === "function" ? v() : v !== false;
  }

  // Target teleport panel filter. Reaktif: kalau pakai ref, nilainya baru
  // tersedia setelah elemen ter-mount (sebelum itu null => panel belum dirender).
  const filterTeleportTarget = computed<string | HTMLElement | null>(() => {
    // `params.filterTeleportTo` belum ada => pakai default. Kalau berupa ref
    // (termasuk computed dari props), unwrap dulu supaya reaktif.
    if (params.filterTeleportTo == null) return DEFAULT_FILTER_TELEPORT_TARGET;
    const target = unref(params.filterTeleportTo) as string | HTMLElement | ComponentPublicInstance | null | undefined;

    // undefined => tidak diisi, pakai default
    if (target === undefined) return DEFAULT_FILTER_TELEPORT_TARGET;
    // null => elemen (ref) belum ter-mount, panel filter belum dirender
    if (target === null) return null;

    if (typeof target === "string") {
      const value = target.trim();
      if (!value) return DEFAULT_FILTER_TELEPORT_TARGET;
      // id polos => jadikan selector id; selector lain dibiarkan apa adanya
      return /^[#.\[]/.test(value) ? value : `#${value}`;
    }

    // ref ke komponen => ambil root element-nya
    return target instanceof HTMLElement ? target : ((target as ComponentPublicInstance).$el ?? null);
  });

  function toggleFilter() {
    isFilterOpen.value = !isFilterOpen.value;
    saveState({ isFilterOpen: isFilterOpen.value });
  }

  function buildPayload(currentFilter: FormModel, currentPage: number, currentLimit: number, currentSearch: string, currentColumns: string[]): DataTableQueryDto {
    const filters = Object.entries(currentFilter)
      .filter(([_, value]) => {
        if (Array.isArray(value)) return value.length > 0;
        return value !== null && value !== "" && value !== undefined;
      })
      .map(([key, value]) => {
        const field = filterFields.find((f) => f.name === key);
        return {
          key,
          type: field?.type ?? "string",
          values: Array.isArray(value) ? value.map(String) : [String(value)],
        } as FilterDto;
      });

    // Pastikan uniqueKey selalu ikut terkirim ke backend walau kolomnya sedang disembunyikan
    const columnsWithUniqueKey = currentColumns.includes(uniqueKey.value) ? currentColumns : [...currentColumns, uniqueKey.value];

    return {
      page: currentPage,
      size: currentLimit,
      offset: (currentPage - 1) * currentLimit,
      filters: filters.length > 0 ? filters : undefined,
      search: currentSearch.trim() !== "" ? currentSearch : undefined,
      columns: columnsWithUniqueKey.length > 0 ? columnsWithUniqueKey : undefined,
      order: sortOrder.value,
      order_by: sortBy.value,
    };
  }

  function handleSorterChange(sorter: any | any[]) {
    const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    if (!currentSorter || !currentSorter.order) {
      sortOrder.value = undefined;
      sortBy.value = undefined;
    } else {
      sortOrder.value = currentSorter.order === "ascend" ? "asc" : "desc";
      sortBy.value = currentSorter.columnKey;
    }

    page.value = 1;
    saveState({
      sortBy: sortBy.value,
      sortOrder: sortOrder.value,
      page: 1,
    });

    applyFilter();
  }

  async function onPageChange(number: number) {
    isDataLoading.value = true;
    page.value = number;
    saveState({ page: number });

    const payload = buildPayload(filterModel, page.value, limit.value, search.value, visibleColumns.value);
    const [items] = await Promise.all([params.dataFetcher(payload)]);

    data.value = items;
    isDataLoading.value = false;
  }

  async function onPageSizeChange(number: number) {
    isDataLoading.value = true;
    isPageLoading.value = true;

    page.value = 1;
    limit.value = number;
    saveState({ limit: number, page: 1 });

    const payloadItems = buildPayload(filterModel, page.value, limit.value, search.value, visibleColumns.value);
    const payloadPage = buildPayload(filterModel, 1, limit.value, search.value, visibleColumns.value);

    const [items, pageInfo] = await Promise.all([params.dataFetcher(payloadItems), params.pageFetcher(payloadPage)]);

    data.value = items;
    total.value = pageInfo.total;
    filteredTotal.value = pageInfo.filteredTotal;

    isDataLoading.value = false;
    isPageLoading.value = false;
  }

  async function applyFilter() {
    isDataLoading.value = true;
    isPageLoading.value = true;
    page.value = 1;

    appliedFilterModel.value = { ...filterModel };
    saveState({ filterModel: { ...filterModel }, page: 1 });

    const payloadItems = buildPayload(filterModel, page.value, limit.value, search.value, visibleColumns.value);
    const payloadPage = buildPayload(filterModel, page.value, limit.value, search.value, visibleColumns.value);

    const [items, pageInfo] = await Promise.all([params.dataFetcher(payloadItems), params.pageFetcher(payloadPage)]);

    data.value = items;
    total.value = pageInfo.total;
    filteredTotal.value = pageInfo.filteredTotal;

    isDataLoading.value = false;
    isPageLoading.value = false;
  }

  async function onSearch(query: string) {
    isDataLoading.value = true;
    isPageLoading.value = true;
    page.value = 1;
    search.value = query;
    saveState({ search: query, page: 1 });

    const payloadItems = buildPayload(filterModel, page.value, limit.value, search.value, visibleColumns.value);
    const payloadPage = buildPayload(filterModel, page.value, limit.value, search.value, visibleColumns.value);

    const [items, pageInfo] = await Promise.all([params.dataFetcher(payloadItems), params.pageFetcher(payloadPage)]);

    data.value = items;
    total.value = pageInfo.total;
    filteredTotal.value = pageInfo.filteredTotal;

    isDataLoading.value = false;
    isPageLoading.value = false;
  }

  async function refresh() {
    isDataLoading.value = true;
    isPageLoading.value = true;

    const payloadItems = buildPayload(filterModel, page.value, limit.value, search.value, visibleColumns.value);
    const payloadPage = buildPayload(filterModel, page.value, limit.value, search.value, visibleColumns.value);

    const [items, pageInfo] = await Promise.all([params.dataFetcher(payloadItems), params.pageFetcher(payloadPage)]);

    data.value = items;
    total.value = pageInfo.total;
    filteredTotal.value = pageInfo.filteredTotal;

    isDataLoading.value = false;
    isPageLoading.value = false;
  }

  async function init() {
    isDataLoading.value = true;
    isPageLoading.value = true;

    // Menggunakan nilai page dan limit terbaru yang sudah dimuat dari localStorage saat init
    const payloadItems = buildPayload(filterModel, page.value, limit.value, search.value, visibleColumns.value);
    const payloadPage = buildPayload(filterModel, page.value, limit.value, search.value, visibleColumns.value);

    const [items, pageInfo] = await Promise.all([params.dataFetcher(payloadItems), params.pageFetcher(payloadPage)]);

    data.value = items;
    total.value = pageInfo.total;
    filteredTotal.value = pageInfo.filteredTotal;

    isDataLoading.value = false;
    isPageLoading.value = false;

    const tableProp = table as unknown as ReturnType<typeof useDataTableWidget<Record<string, any>>>;

    headComponent.value = h(DataTableHeadWidget, { table: tableProp });
    bodyComponent.value = h(DataTableBodyWidget, { table: tableProp });
    footerComponent.value = h(DataTableFooterWidget, { table: tableProp });
  }

  function paginationPrefix(info: PaginationInfo) {
    if (info.itemCount === 0) {
      const base = `0-0 of 0`;
      if (total.value !== filteredTotal.value) {
        return `${base} (filtered from ${total.value})`;
      }
      return base;
    }

    const start = info.startIndex + 1;
    const end = info.endIndex + 1;
    const base = `${start}-${end} of ${info.itemCount}`;

    if (total.value !== filteredTotal.value) {
      return `${base} (filtered from ${total.value})`;
    }

    return base;
  }

  // ===== Selection (checkbox) + bulk action =====
  type RowKey = string | number;
  const selection = params.selection;
  const isSelectionEnabled = !!selection?.enable;
  const bulkActions = params.bulkActions ?? [];

  const checkedRowKeys = ref<RowKey[]>([]);
  // Simpan data row terpilih, supaya pilihan tetap utuh walau pindah halaman (remote pagination)
  const selectedMap = shallowRef(new Map<RowKey, T>());
  const selectedRows = computed(() => Array.from(selectedMap.value.values()));
  const selectedCount = computed(() => checkedRowKeys.value.length);
  const bulkLoadingKey = ref<string | null>(null);

  function rowKey(row: T): RowKey {
    return row[uniqueKey.value] as RowKey;
  }

  function handleCheckedRowKeysChange(keys: RowKey[]) {
    const keySet = new Set(keys);
    const next = new Map<RowKey, T>();
    selectedMap.value.forEach((row, k) => {
      if (keySet.has(k)) next.set(k, row);
    });
    // `data` adalah ref<T[]>, elemennya bertipe UnwrapRefSimple<T>, jadi perlu di-cast ke T[]
    (data.value as T[]).forEach((row) => {
      const k = rowKey(row);
      if (keySet.has(k)) next.set(k, row);
    });
    selectedMap.value = next;
    checkedRowKeys.value = keys;
  }

  function clearSelection() {
    checkedRowKeys.value = [];
    selectedMap.value = new Map();
  }

  function isBulkActionVisible(action: DataTableBulkAction<T>): boolean {
    const v = action.visible;
    return typeof v === "function" ? v(selectedRows.value) : v !== false;
  }

  function isBulkActionDisabled(action: DataTableBulkAction<T>): boolean {
    const d = action.disabled;
    return typeof d === "function" ? d(selectedRows.value) : d === true;
  }

  async function runBulkAction(action: DataTableBulkAction<T>) {
    if (bulkLoadingKey.value || selectedRows.value.length === 0) return;
    bulkLoadingKey.value = action.key;
    try {
      await action.onClick(selectedRows.value);
      if (action.clearAfter !== false) {
        clearSelection();
        await refresh();
      }
    } finally {
      bulkLoadingKey.value = null;
    }
  }

  // Klik di kolom checkbox tidak boleh memicu onRowClick (navigasi ke detail)
  function rowProps(row: T) {
    return {
      style: { cursor: onRowClick != null ? "pointer" : "default" },
      onClick: (e: MouseEvent) => {
        if ((e.target as HTMLElement | null)?.closest(".n-data-table-td--selection")) return;
        onRowClick?.(row);
      },
    };
  }

  const namedColumns = computed<NamedDataTableColumn<T>[]>(() => {
    return columns.value.filter(isNamedColumn);
  });

  const dataVisibleColumns = computed(() => {
    return visibleColumns.value
      .map((key) => {
        const col = namedColumns.value.find((c) => c.key === key);
        if (!col) return null;

        if (sortBy.value === key && sortOrder.value) {
          return {
            ...col,
            defaultSortOrder: sortOrder.value === "asc" ? ("ascend" as const) : ("descend" as const),
          };
        }

        const copy = { ...col };
        if ("defaultSortOrder" in copy) {
          delete copy.defaultSortOrder;
        }
        return copy;
      })
      .filter((c): c is NamedDataTableColumn<T> & { defaultSortOrder?: "ascend" | "descend" } => !!c);
  });

  // Kolom checkbox (fixed left) ditambahkan di depan kalau selection aktif
  const computedVisibleColumns = computed<any[]>(() => {
    if (!isSelectionEnabled) return dataVisibleColumns.value;
    return [
      {
        type: "selection",
        fixed: "left",
        width: 48,
        disabled: selection?.disabled,
      },
      ...dataVisibleColumns.value,
    ];
  });

  const computedInvisibleColumns = computed(() => {
    return namedColumns.value.filter((c) => !visibleColumns.value.includes(c.key));
  });

  const draftVisibleColumns = ref<string[]>([...visibleColumns.value]);

  function openColumnSettings() {
    draftVisibleColumns.value = [...visibleColumns.value];
    isShowVisibleColumnModal.value = true;
  }

  function setDraftVisibleColumns(keys: Array<string | number>) {
    // Minimal harus ada 1 kolom yang tampil: abaikan update kalau kosong.
    if (keys.length === 0) return;
    draftVisibleColumns.value = keys.map(String);
  }

  function confirmColumnVisibility() {
    visibleColumns.value = [...draftVisibleColumns.value];
    saveState({ visibleColumns: [...visibleColumns.value] });
    isShowVisibleColumnModal.value = false;
    refresh();
  }

  function cancelColumnVisibility() {
    draftVisibleColumns.value = [...visibleColumns.value];
    isShowVisibleColumnModal.value = false;
  }

  // Opsi pengaturan kolom: semua kolom bernama, urutannya sesuai definisi kolom (panel kiri).
  const draftColumnOptions = computed<{ key: string; label: string }[]>(() => {
    return namedColumns.value.map((col) => ({
      key: col.key,
      label: columnLabel(col),
    }));
  });

  // ===== Export =====
  const isExportEnabled = !!params.export && params.export.enable !== false;

  const isShowExportModal = ref(false);
  const draftExportColumns = ref<string[]>([]);
  const exportFilterModel = reactive<FormModel>({});
  // Filter export: default tidak ada; user menambahkan lewat popup checkbox
  const draftExportFilterFields = ref<string[]>([]);

  /** Opsi sheet rincian (relasi one-to-many) yang bisa di-checkbox di modal export */
  const exportDetailSheetOptions = computed<DataTableExportDetailSheet[]>(() => {
    return (params.export?.detailSheets ?? []).map((s) => ({ ...s, label: s.label ?? s.name }));
  });
  const draftExportSheets = ref<string[]>([]);

  const exportFilterFields = computed<FormField[]>(() => {
    return filterFields.filter((f) => draftExportFilterFields.value.includes(f.name as string));
  });

  const exportColumnOptions = computed(() => {
    return namedColumns.value.map((col) => ({
      key: col.key,
      label: columnLabel(col),
      // uniqueKey (id) wajib ikut terexport
      locked: col.key === uniqueKey.value,
    }));
  });

  function openExportModal() {
    // Default kolom: kolom yang sedang tampil (+ id)
    draftExportColumns.value = visibleColumns.value.includes(uniqueKey.value)
      ? [...visibleColumns.value]
      : [uniqueKey.value, ...visibleColumns.value];

    // Default sheet rincian: semua terpilih
    draftExportSheets.value = exportDetailSheetOptions.value.map((s) => s.key);

    // Default filter: kosong
    Object.keys(exportFilterModel).forEach((k) => delete exportFilterModel[k]);
    draftExportFilterFields.value = [];

    isShowExportModal.value = true;
  }

  function toggleExportSheet(key: string, next: boolean) {
    const isSelected = draftExportSheets.value.includes(key);
    if (next && !isSelected) {
      draftExportSheets.value = [...draftExportSheets.value, key];
    } else if (!next && isSelected) {
      draftExportSheets.value = draftExportSheets.value.filter((k) => k !== key);
    }
  }

  function setExportFilterFields(names: Array<string | number>) {
    const next = names.map(String);

    // Bersihkan nilai model field yang dihapus supaya tidak ikut ke payload export
    draftExportFilterFields.value.filter((n) => !next.includes(n)).forEach((n) => {
      delete exportFilterModel[n];
    });

    draftExportFilterFields.value = next;
  }

  function toggleExportColumn(key: string, next: boolean) {
    if (key === uniqueKey.value) return; // id mandatory: tidak boleh diubah
    const isSelected = draftExportColumns.value.includes(key);

    if (next && !isSelected) {
      draftExportColumns.value = [...draftExportColumns.value, key];
    } else if (!next && isSelected) {
      if (draftExportColumns.value.length <= 1) return; // minimal 1 kolom terpilih
      draftExportColumns.value = draftExportColumns.value.filter((k) => k !== key);
    }
  }

  function buildExportPayload(colKeys: string[]): DataTableExportPayload {
    const dto = buildPayload(exportFilterModel, 1, 1, search.value, colKeys);
    return {
      columns: dto.columns ?? colKeys,
      filters: dto.filters,
      search: dto.search,
      order: dto.order,
      order_by: dto.order_by,
    };
  }

  function confirmExport() {
    // uniqueKey (id) selalu ikut dan berada di depan
    const cols = draftExportColumns.value.includes(uniqueKey.value) ? [...draftExportColumns.value] : [uniqueKey.value, ...draftExportColumns.value];

    const payload = buildExportPayload(cols);
    payload.detailSheets = [...draftExportSheets.value];
    isShowExportModal.value = false;
    void params.export?.onClick(payload);
  }

  function cancelExport() {
    isShowExportModal.value = false;
  }

  const activeFilterCount = computed(() => {
    return Object.entries(appliedFilterModel.value).filter(([key, v]) => {
      if (v === null || v === undefined || v === "") return false;
      if (Array.isArray(v)) return v.length > 0;
      const field = filterFields.find((f) => f.name === key);
      if (field?.type === "number" && v === 0) return false;
      return true;
    }).length;
  });

  const table = {
    columns,
    data,
    total,
    filteredTotal,

    page,
    limit,
    search,
    isFilterOpen,

    size,
    setSize,

    sortBy,
    sortOrder,
    handleSorterChange,

    isDataLoading,
    isPageLoading,
    onRowClick,

    pageSizes,
    onPageChange,
    onPageSizeChange,
    onSearch,
    refresh,
    toggleFilter,
    init,
    pageCount,

    // 3 bagian datatable (dipakai terpisah)
    headComponent,
    bodyComponent,
    footerComponent,

    paginationPrefix,

    filterFields,
    applyFilter,
    filterModel,
    appliedFilterModel,
    resetFilter,
    visibleColumns,
    computedVisibleColumns,
    computedInvisibleColumns,
    isShowVisibleColumnModal,

    draftVisibleColumns,
    draftColumnOptions,
    openColumnSettings,
    setDraftVisibleColumns,
    confirmColumnVisibility,
    cancelColumnVisibility,
    columnLabel,

    create,
    actions,
    isActionVisible,

    // selection + bulk action
    isSelectionEnabled,
    checkedRowKeys,
    selectedRows,
    selectedCount,
    handleCheckedRowKeysChange,
    clearSelection,
    rowKey,
    rowProps,
    bulkActions,
    bulkLoadingKey,
    isBulkActionVisible,
    isBulkActionDisabled,
    runBulkAction,

    filterTeleportTarget,

    maxHeight,
    fillHeight,
    bottomOffset,

    activeFilterCount,

    // export
    isExportEnabled,
    isShowExportModal,
    draftExportColumns,
    exportFilterModel,
    exportColumnOptions,
    draftExportFilterFields,
    exportFilterFields,
    setExportFilterFields,
    exportDetailSheetOptions,
    draftExportSheets,
    toggleExportSheet,
    openExportModal,
    toggleExportColumn,
    buildExportPayload,
    confirmExport,
    cancelExport,
  };

  return table;
}
